/**
 * Domain-split configuration.
 *
 * In production Viyora is served from TWO domains:
 *   • MARKETING (public)   → https://viyora.co.in
 *       Landing, Pricing, Features, Blog, Legal (Privacy/Terms/DPDP),
 *       PublicBooking, PatientRecord.
 *   • APP (authenticated)  → https://app.viyora.co.in
 *       /login, /signup, /app/*, TrialEnded, and every route that
 *       requires an authenticated session.
 *
 * In the preview / local environment both surfaces are served from the
 * SAME origin so we transparently fall back to `window.location.origin`.
 * This module is the single source of truth used everywhere in the SPA
 * to build cross-domain URLs — never hard-code `viyora.co.in`.
 *
 * Configuration (set on the build environment):
 *   REACT_APP_MARKETING_URL   e.g.  "https://viyora.co.in"
 *   REACT_APP_APP_URL         e.g.  "https://app.viyora.co.in"
 * If either is empty / unset, the helper falls back to same-origin
 * relative URLs (preview-friendly, no-op behaviour).
 */

const MARKETING_URL = (process.env.REACT_APP_MARKETING_URL || "").replace(/\/$/, "");
const APP_URL       = (process.env.REACT_APP_APP_URL       || "").replace(/\/$/, "");

// Hostnames we recognise. Everything else is "preview / dev" and gets no
// cross-domain redirect / rewrite (same-origin).
function _hostFromUrl(url) {
  try { return new URL(url).hostname; } catch { return ""; }
}
const MARKETING_HOST = _hostFromUrl(MARKETING_URL);
const APP_HOST       = _hostFromUrl(APP_URL);

/** True when we're currently on the public marketing domain. */
export function isOnMarketingHost() {
  if (typeof window === "undefined") return false;
  return !!MARKETING_HOST && window.location.hostname === MARKETING_HOST;
}

/** True when we're currently on the authenticated app domain. */
export function isOnAppHost() {
  if (typeof window === "undefined") return false;
  return !!APP_HOST && window.location.hostname === APP_HOST;
}

/** Build a fully-qualified marketing URL (falls back to same-origin path). */
export function marketingUrl(path = "/") {
  const p = path.startsWith("/") ? path : `/${path}`;
  return MARKETING_URL ? `${MARKETING_URL}${p}` : p;
}

/** Build a fully-qualified app URL (falls back to same-origin path). */
export function appUrl(path = "/") {
  const p = path.startsWith("/") ? path : `/${path}`;
  return APP_URL ? `${APP_URL}${p}` : p;
}

/** True when both domains are configured AND distinct. */
export function isDomainSplitActive() {
  return !!MARKETING_HOST && !!APP_HOST && MARKETING_HOST !== APP_HOST;
}

/**
 * Route classification used by <DomainRouter> to decide which host owns
 * each pathname.  Non-authenticated public surfaces live on marketing;
 * the login/signup screens and every `/app/*` route live on app.
 */
export function belongsToApp(pathname) {
  if (!pathname) return false;
  return (
    pathname === "/login" ||
    pathname === "/signup" ||
    pathname === "/forgot-password" ||
    pathname === "/reset-password" ||
    pathname === "/trial-ended" ||
    pathname === "/verify-otp" ||
    pathname.startsWith("/app/") ||
    pathname.startsWith("/app")
  );
}
export function belongsToMarketing(pathname) {
  if (!pathname) return true;
  if (belongsToApp(pathname)) return false;
  // Public-facing patient links (booking + record) can live on either
  // domain safely — leave them wherever the user landed.
  if (pathname.startsWith("/book/") || pathname.startsWith("/patient-record/")) {
    return false;
  }
  return true;
}
